// packages/shared/src/hooks/useApplicationInsights.ts
import { useEffect, useRef, useState } from 'react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';
import { isDevelopment, isProduction, getEnvVar } from '../utils/env';

interface ApplicationInsightsConfig {
  connectionString: string;
  instrumentationKey?: string;
  enableAutoRouteTracking?: boolean;
  enableCorsCorrelation?: boolean;
  enableRequestHeaderTracking?: boolean;
  enableResponseHeaderTracking?: boolean;
  enableAjaxErrorStatusText?: boolean;
  enableAjaxPerfTracking?: boolean;
  enableUnhandledPromiseRejectionTracking?: boolean;
  samplingPercentage?: number;
  maxAjaxCallsPerView?: number;
  disableTelemetry?: boolean;
  userContext?: string;
  customProperties?: Record<string, any>;
}

interface ApplicationInsightsHook {
  appInsights: ApplicationInsights | null;
  isInitialized: boolean;
  trackEvent: (
    eventName: string,
    properties?: Record<string, any>,
    measurements?: Record<string, number>
  ) => void;
  trackException: (error: Error, properties?: Record<string, any>) => void;
  trackPageView: (name?: string, properties?: Record<string, any>) => void;
  trackMetric: (name: string, average: number, properties?: Record<string, any>) => void;
  // trackDependency: (dependencyTypeName: string, name: string, data: string, success: boolean, duration: number, properties?: Record<string, any>) => void;
  setUserContext: (userId: string, accountId?: string) => void;
  clearUserContext: () => void;
}

const useApplicationInsights = (config: ApplicationInsightsConfig): ApplicationInsightsHook => {
  const [appInsights, setAppInsights] = useState<ApplicationInsights | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const initializationRef = useRef(false);
  const reactPluginRef = useRef<ReactPlugin | null>(null);

  useEffect(() => {
    // Prevent double initialization
    if (initializationRef.current || !config.connectionString) {
      return;
    }

    initializationRef.current = true;

    try {
      // Create browser history for route tracking
      const browserHistory = createBrowserHistory();

      // Create React plugin for SPA tracking
      const reactPlugin = new ReactPlugin();
      reactPluginRef.current = reactPlugin;

      // Initialize Application Insights
      const ai = new ApplicationInsights({
        config: {
          connectionString: config.connectionString,
          extensions: [reactPlugin as any],
          extensionConfig: {
            [reactPlugin.identifier]: {
              history: browserHistory,
              enableAutoRouteTracking: config.enableAutoRouteTracking ?? true,
            },
          },
          disableTelemetry: config.disableTelemetry ?? false,
          samplingPercentage: config.samplingPercentage ?? (isProduction() ? 100 : 50),
          loggingLevelConsole: isDevelopment() ? 2 : 0,
        },
      });

      // Load Application Insights
      ai.loadAppInsights();
      ai.trackPageView();

      // Set user context if provided
      if (config.userContext) {
        ai.setAuthenticatedUserContext(config.userContext);
      }

      setAppInsights(ai);
      setIsInitialized(true);

      if (isDevelopment()) {
        console.log('Application Insights initialized successfully');
      }

      // Track initialization
      ai.trackEvent(
        { name: 'ApplicationInsights_Initialized' },
        {
          environment: getEnvVar('NODE_ENV') || getEnvVar('VITE_NODE_ENV') || 'unknown',
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        }
      );
    } catch (error) {
      console.error('Failed to initialize Application Insights:', error);
      setIsInitialized(false);
    }

    // Cleanup function
    // eslint-disable-next-line consistent-return
    return () => {
      if (appInsights) {
        appInsights.unload();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.connectionString, config.instrumentationKey]);

  // Tracking methods
  const trackEvent = (
    eventName: string,
    properties?: Record<string, any>,
    measurements?: Record<string, number>
  ) => {
    if (appInsights && isInitialized) {
      appInsights.trackEvent({ name: eventName }, { ...properties, measurements });
    } else if (isDevelopment()) {
      console.log('ApplicationInsights Event:', eventName, properties, measurements);
    }
  };

  const trackException = (error: Error, properties?: Record<string, any>) => {
    if (appInsights && isInitialized) {
      appInsights.trackException(
        {
          exception: error,
          severityLevel: 3, // Error level
        },
        properties
      );
    } else if (isDevelopment()) {
      console.error('ApplicationInsights Exception:', error, properties);
    }
  };

  const trackPageView = (name?: string, properties?: Record<string, any>) => {
    if (appInsights && isInitialized) {
      appInsights.trackPageView({ name, properties });
    } else if (isDevelopment()) {
      console.log('ApplicationInsights PageView:', name, properties);
    }
  };

  const trackMetric = (name: string, average: number, properties?: Record<string, any>) => {
    if (appInsights && isInitialized) {
      appInsights.trackMetric({ name, average, properties });
    } else if (isDevelopment()) {
      console.log('ApplicationInsights Metric:', name, average, properties);
    }
  };

  const setUserContext = (userId: string, accountId?: string) => {
    if (appInsights && isInitialized) {
      appInsights.setAuthenticatedUserContext(userId, accountId);
    }
  };

  const clearUserContext = () => {
    if (appInsights && isInitialized) {
      appInsights.clearAuthenticatedUserContext();
    }
  };

  return {
    appInsights,
    isInitialized,
    trackEvent,
    trackException,
    trackPageView,
    trackMetric,
    setUserContext,
    clearUserContext,
  };
};

export default useApplicationInsights;
export type { ApplicationInsightsConfig, ApplicationInsightsHook };
